import React, { useState } from 'react';
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, TableSortLabel, CircularProgress, Box, Chip, Tooltip, Typography, IconButton, TextField } from '@mui/material';
import { TabPanel} from "@mui/lab";
import { SearchOutlined, Edit, Close, Check } from "@mui/icons-material";
import styled from "@emotion/styled";
import useSortableTable from "../utils/useSortableTable";

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: theme.spacing(0),
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const overageRateFor = (terms) => {
  const hours = Number(terms?.hours) || 0;
  const charge = Number(terms?.charge) || 0;
  switch (terms?.overageRate?.mode) {
    case 'Retainer Rate':
      return hours > 0 ? charge / hours : 0;
    case 'Custom Rate':
      return Number(terms?.overageRate?.amount) || 0;
    default:
      return null;
  }
};

const overageFor = (terms, hoursUsed, hoursPurchased) => {
  const extra = hoursUsed - hoursPurchased;
  if (extra <= 0) return { amount: 0, extra: 0, note: 'No hours past the pool.' };
  if (!terms?.overage) return { amount: 0, extra, note: `${extra.toFixed(2)} hours past the pool, but overage billing is off so they are not charged.` };
  const rate = overageRateFor(terms);
  if (rate === null) {
    return { amount: null, extra, note: `${extra.toFixed(2)} hours past the pool, charged at each skill's retainer rate. The total depends on which skills logged them, so it cannot be shown here.` };
  }
  return { amount: extra * rate, extra, note: `${extra.toFixed(2)} hours past the pool at $${rate.toFixed(2)} per hour.` };
};

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  paddingTop: "2px",
}));

const ParentPath = styled(Typography)(({ theme }) => ({
  display: 'block',
  color: theme.palette.text.secondary,
}));

const TYPE_CHIP_COLOR = { Client: 'primary', Account: 'secondary', Group: 'info' };

const parentPath = (retainer) => {
  const clientName = retainer.clientName || (typeof retainer.client === 'object' ? retainer.client?.name : null);
  const accountName = retainer.accountName || (typeof retainer.account === 'object' ? retainer.account?.name : null);
  return [clientName, retainer.type === 'Group' ? accountName : null].filter(Boolean).join(' › ');
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  maxWidth: "100px",
  textAlign: "right",
  "& .MuiInputBase-input[type=number]": {
    textAlign: "right",
    padding: theme.spacing(1),
  },
}));

const RetainersTable = ({ data, loading, hasFetched, onSave }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [editedRetainerId, setEditedRetainerId] = useState(null);
  const [editedRetainer, setEditedRetainer] = useState(null);
  const [saving, setSaving] = useState(false);

  const { sorted, order, handleSort } = useSortableTable(data, {
    name: r => r.name,
    type: r => r.type,
    hoursCommitted: r => r.billingTerms?.hours || 0,
    hoursPosted: r => (r.time || 0) / 60,
    utilization: r => {
      const hp = r.billingTerms?.hours || 0;
      const hu = (r.time || 0) / 60;
      return hp > 0 ? (hu / hp) * 100 : 0;
    },
    cost: r => r.cost || 0,
    revenue: r => r.billingTerms?.charge || 0,
  });

  const handleEditClick = (retainer, uniqueId) => {
    setEditedRetainerId(uniqueId);
    setEditedRetainer({ ...retainer });
  };

  const handleCloseEdit = () => {
    setEditedRetainerId(null);
    setEditedRetainer(null);
  };

  const handleSaveEdit = () => {
    if (!editedRetainer || !onSave) return;
    setSaving(true);
    onSave({
      id: editedRetainer._id,
      type: editedRetainer.type,
      billingTerms: { ...editedRetainer.billingTerms },
    }, (success) => {
      setSaving(false);
      if (success) {
        setEditedRetainerId(null);
        setEditedRetainer(null);
      }
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    // BUG FIX (B14): second arg of parseInt is the radix, not a default.
    // Previous code passed 25 — values like "50" parsed as base-25 -> 125,
    // silently breaking pagination when the user picked 50 or 100.
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function getUtilizationColor(utilization) {
    const u = Number(utilization);
    if (u < 70 || u > 130) return "error";
    if (u < 90 || u > 110) return "warning";
    return "success";
  }

  function getMarginColor(margin) {
    const m = Number(margin);
    if (m < 30) return "error";
    if (m < 50) return "warning";
    return "success";
  }

  return (
          <StyledTabPanel value="retainer" >
            <Paper >
              <TableContainer>
                {loading ? (
                  <LoadingBox>
                    <CircularProgress />
                  </LoadingBox>
                ) : (
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow key="header">
                        <TableCell sortDirection={order.key === 'name' ? order.dir : false}>
                          <TableSortLabel active={order.key === 'name'} direction={order.key === 'name' ? order.dir : 'asc'} onClick={() => handleSort('name')}>Retainer Pool</TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={order.key === 'type' ? order.dir : false}>
                          <TableSortLabel active={order.key === 'type'} direction={order.key === 'type' ? order.dir : 'asc'} onClick={() => handleSort('type')}>Type</TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={order.key === 'hoursCommitted' ? order.dir : false}>
                          <TableSortLabel active={order.key === 'hoursCommitted'} direction={order.key === 'hoursCommitted' ? order.dir : 'asc'} onClick={() => handleSort('hoursCommitted')}>Hours Committed</TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={order.key === 'hoursPosted' ? order.dir : false}>
                          <TableSortLabel active={order.key === 'hoursPosted'} direction={order.key === 'hoursPosted' ? order.dir : 'asc'} onClick={() => handleSort('hoursPosted')}>Hours Posted</TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={order.key === 'utilization' ? order.dir : false}>
                          <TableSortLabel active={order.key === 'utilization'} direction={order.key === 'utilization' ? order.dir : 'asc'} onClick={() => handleSort('utilization')}>Utilization %</TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={order.key === 'cost' ? order.dir : false}>
                          <TableSortLabel active={order.key === 'cost'} direction={order.key === 'cost' ? order.dir : 'asc'} onClick={() => handleSort('cost')}>Cost</TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sortDirection={order.key === 'revenue' ? order.dir : false}>
                          <TableSortLabel active={order.key === 'revenue'} direction={order.key === 'revenue' ? order.dir : 'asc'} onClick={() => handleSort('revenue')}>Revenue</TableSortLabel>
                        </TableCell>
                        <TableCell align='right'>Overage</TableCell>
                        <TableCell align="right">Margin %</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((retainer, index) => {
                        const hoursPurchased = retainer.billingTerms?.hours || 0;
                        const hoursUsed = (retainer.time/60) || 0;
                        const utilization = hoursPurchased > 0 ? ((hoursUsed / hoursPurchased) * 100).toFixed(1) : "0.0";
                        const cost = retainer?.cost?.toFixed(2) || 0;
                        const revenue = retainer?.billingTerms?.charge?.toFixed(2) || 0;
                        const overage = overageFor(retainer.billingTerms, hoursUsed, hoursPurchased);

                        const totalRevenue = Number(revenue) + (overage.amount || 0);
                        const margin = totalRevenue > 0 ? (((totalRevenue - cost) / totalRevenue) * 100).toFixed(2) : "0.00";
                        const uniqueId = retainer._id || retainer.id || `retainer-${page * rowsPerPage + index}`;
                        const path = parentPath(retainer);

                        return (
                          <TableRow key={uniqueId} hover>
                            <TableCell>
                              <Typography variant="body1">{retainer.name}</Typography>
                              {path && <ParentPath variant="caption">under {path}</ParentPath>}
                            </TableCell>
                            <TableCell>
                              <StyledChip label={retainer.type} size="small" color={TYPE_CHIP_COLOR[retainer.type] || "default"} />
                            </TableCell>
                            <TableCell align="right">
                              {editedRetainerId === uniqueId ? (
                                <StyledTextField type="number" value={editedRetainer?.billingTerms?.hours || ''} onChange={(e) => setEditedRetainer({ ...editedRetainer, billingTerms: { ...editedRetainer.billingTerms, hours: e.target.value } })} />
                              ) : (
                                <Typography variant="body1">{hoursPurchased}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{hoursUsed.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <StyledChip label={`${utilization}%`} size="small" color={getUtilizationColor(utilization)} />
                            </TableCell>
                            <TableCell align="right"><Typography variant="body1">$ {cost}</Typography></TableCell>
                            <TableCell align="right">
                              {editedRetainerId === uniqueId ? (
                                <StyledTextField type="number" value={editedRetainer?.billingTerms?.charge || ''} onChange={(e) => setEditedRetainer({ ...editedRetainer, billingTerms: { ...editedRetainer.billingTerms, charge: e.target.value } })} />
                              ) : (
                                <Typography variant="body1">$ {revenue}</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={overage.note}>
                                <Typography variant="body1">{overage.amount === null ? "—" : `$ ${overage.amount.toFixed(2)}`}</Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <StyledChip label={`${margin}%`} size="small" color={getMarginColor(margin)} />
                            </TableCell>
                            <TableCell>
                              {editedRetainerId === uniqueId ? (
                                <Box>
                                  <Tooltip title="Save the hours and revenue for this retainer">
                                    <span>
                                      <IconButton onClick={handleSaveEdit} disabled={saving}>
                                        <Check />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Discard these changes">
                                    <span>
                                      <IconButton onClick={handleCloseEdit} disabled={saving}>
                                        <Close />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              ) : (
                              <Tooltip title={`Edit the hours and revenue on this ${(retainer.type || 'retainer').toLowerCase()} retainer`}>
                                <IconButton onClick={() => handleEditClick(retainer, uniqueId)} >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {data.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            <EmptyStateBox>
                              <SearchOutlined />
                              <Typography variant="body1">{hasFetched ? 'No client, account or group retainers match the current filters' : 'Click Generate Report to load data'}</Typography>
                            </EmptyStateBox>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>

              {!loading && data.length > 25 &&
                <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={data.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
              }
            </Paper>
          </StyledTabPanel>
    );
};

export default RetainersTable;